import { isEmpty } from 'lodash';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';

import { Tasks } from '@air/api';

import { ZippingTasksPanel } from '~/components/ZippingTasks/ZippingTasksPanel';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useReloadApplication } from '~/providers/ReloadApplicationProvider';
import { useSocketConnectionChange } from '~/providers/SocketContext/hooks/useSocketConnectionChange';
import { removeTasksAction } from '~/store/tasks/actions';
import { makeZippingTasksSelectorByWorkspaceId } from '~/store/tasks/selectors';
import { ReadyState } from '~/types/sockets';
import { useAirSelector } from '~/utils/ReduxUtils';
import { useCancelTask } from '~/utils/taskUtils/useCancelTask';
import { usePrivateSyncTasks } from '~/utils/taskUtils/usePrivateSyncTasks';

export const PrivateZippingTasksPanel = memo(() => {
  const { currentWorkspace } = useCurrentWorkspace();
  const tasksSelector = useMemo(
    () => makeZippingTasksSelectorByWorkspaceId(currentWorkspace?.id),
    [currentWorkspace?.id],
  );
  const dispatch = useDispatch();
  const zippingTasks = useAirSelector(tasksSelector);
  const { cancelTask } = useCancelTask();
  const { onAddPendingAction, onRemovePendingAction } = useReloadApplication();

  const getDownloadUrl = useCallback(
    (taskId: string | undefined) => {
      if (!taskId) {
        throw new Error('No task id');
      }

      const workspaceId = currentWorkspace?.id;

      if (!workspaceId) {
        throw new Error('No workspace id');
      }

      return Tasks.ContentExporter.getDownloadUrl({ workspaceId, taskId });
    },
    [currentWorkspace?.id],
  );

  const {
    loadFromStorage,
    syncLocalTasks,
    subscriptionsReady: taskSubscriptionsReady,
  } = usePrivateSyncTasks({
    workspaceId: currentWorkspace?.id,
    tasksSelector,
    localType: 'ZippingForDownload',
    remoteType: 'ContentExporter',
    onComplete: async ({ task, enrichMetadata }) => {
      const { downloadUrl } = await getDownloadUrl(task.id);

      enrichMetadata({
        url: downloadUrl,
      });
    },
    onUpdate: async ({ task, enrichMetadata }) => {
      if (task.data?.secondsRemaining) {
        enrichMetadata({
          secondsRemaining: task.data.secondsRemaining,
        });
      }
    },
  });

  const syncExecutedForReadyStateRef = useRef(false);

  /**
   * On mount, take the tasks from local storage and add them to Redux
   */
  useEffect(() => {
    if (currentWorkspace?.id) {
      loadFromStorage();
    }
  }, [loadFromStorage, currentWorkspace?.id]);

  const syncLocalTasksOnce = useCallback(() => {
    if (!syncExecutedForReadyStateRef.current) {
      syncLocalTasks();
      syncExecutedForReadyStateRef.current = true;
    }
  }, [syncLocalTasks]);

  /**
   * Reset sync flag when task subscriptions become not ready
   */
  useEffect(() => {
    if (!taskSubscriptionsReady) {
      syncExecutedForReadyStateRef.current = false;
    }
  }, [taskSubscriptionsReady]);

  /**
   * Sync tasks when WebSocket reconnects to catch any missed completion events.
   * Only sync when task subscriptions are confirmed ready to avoid race conditions.
   */
  useSocketConnectionChange({
    onChange: (readyState) => {
      if (readyState === ReadyState.OPEN && taskSubscriptionsReady) {
        syncLocalTasksOnce();
      }
    },
  });

  /**
   * Also sync when task subscriptions become ready after reconnection.
   */
  useEffect(() => {
    if (taskSubscriptionsReady) {
      syncLocalTasksOnce();
    }
  }, [taskSubscriptionsReady, syncLocalTasksOnce]);

  useEffect(() => {
    if (!isEmpty(zippingTasks)) {
      onAddPendingAction({ id: 'zipping', description: 'Zipping files' });
    } else {
      onRemovePendingAction('zipping');
    }
  }, [onAddPendingAction, onRemovePendingAction, zippingTasks]);

  /**
   * When this panel unmounts (user logs out most likely),
   * reset the state
   */
  useUnmount(() => {
    dispatch(
      removeTasksAction({
        taskIds: zippingTasks.map(({ localTaskId }) => localTaskId),
      }),
    );
  });

  if (zippingTasks.length < 1) {
    return null;
  }

  return (
    <ZippingTasksPanel
      onHandleExpiredLink={getDownloadUrl}
      zippingTasks={zippingTasks}
      onCancel={cancelTask}
      onClear={cancelTask}
    />
  );
});

PrivateZippingTasksPanel.displayName = 'PrivateZippingTasksPanel';
